import React, { useContext, useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from 'classnames';

import { useDispatch } from "react-redux";

import { Link, Outlet, useNavigate, useParams } from "react-router-dom";

import styles from "./skills.module.scss";
import { createSkill, deleteSkill, fetchSkills, invalidateCreateSkill, invalidateDeleteSkill, updateSkill } from "src/redux/actions/coaching/settings";
import { useSelector } from "react-redux";
import { Skill } from "src/types/coaching/courses";
import Text from "src/components/text";
import { AutoGrowInput, Input } from "src/components/input";
import Actions from "src/components/actionbar";
import { animated, useTransition } from "@react-spring/web";
import Reveal from "src/components/animation";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { rectSwappingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import Button from "src/components/button";
import { invalidateUpdateSkill } from "../../../../redux/actions/coaching/settings";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";
import Slider from "src/components/slider";
import InfiniteScroll from "src/components/infiniteScroll";
import pluralize from "pluralize";
import TimeAgo from "react-timeago";
import EmptyMessage from "src/components/emptyMessage";
import { title } from "process";


interface SkillContextProps {
  skill: Skill | null;
  hasChanges: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (skill: Skill | null) => void;

  // eslint-disable-next-line no-unused-vars
  setHasChanges: (hasChanges: boolean) => void;
}
const SkillContext = React.createContext<SkillContextProps>({ skill: null, hasChanges: false, onChange: () => { }, setHasChanges: () => { } });


export const SkillSettingsPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams();
  console.log("id", id);

  const { skills, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const skillsState = _.get(state, 'coaching.settings.skills');

    const skills: Skill[] = _.get(skillsState, 'items') || [];
    const loading = _.get(skillsState, 'loading');
    const pagination = _.get(skillsState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      skills,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  const [skillContext, setSkillContext] = useState<SkillContextProps>({
    skill: null,
    hasChanges: false,
    onChange: () => { },
    setHasChanges: () => { }
  });

  const currentSkill = useSelector((state) => {
    const skillsState = _.get(state, 'coaching.settings.skills');
    const skills: Skill[] = _.get(skillsState, 'items') || [];
    return skills.find((s: Skill) => s.id === id) || null;
  }, _.isEqual);

  const { creating, createdSkill } = useSelector((state) => {
    return {
      creating: _.get(state, 'coaching.settings.skills.creation.creating') || false,
      createdSkill: _.get(state, 'coaching.settings.skills.creation.item') as any as Skill | null
    }
  });

  const { saving, saved } = useSelector((state) => {
    return {
      saving: _.get(state, 'coaching.settings.skills.update.updating') || false,
      saved: _.get(state, 'coaching.settings.skills.update.updated') || false
    }
  });

  useEffect(() => {
    fetchSkillsForPage(0);

    setSkillContext((prev) => ({
      ...prev,
      onChange: handleSkillChange,
      setHasChanges: handleHasChanges,
    }));
  }, []);

  useEffect(() => {
    if (!id && _.size(skills) > 0) {
      const first = skills[0];
      navigate(`/settings/skills/${first.id}`);
    }

    if(!id){
      setSkillContext((prev) => ({
        ...prev,
        skill: null,
        hasChanges: false,
      }));
    }
  }, [id, skills]);

  useEffect(() => {
    if (createdSkill) {
      navigate(`/settings/skills/${createdSkill!.id!}`);
      dispatch(invalidateCreateSkill());
      toast.success("Skill created successfully!");
    }
  }, [createdSkill]);

  useEffect(() => {
    if (saved) {
      dispatch(invalidateUpdateSkill());
      handleHasChanges(false);
      toast.success("Skill updated successfully!");
    }
  }, [saved]);

  const addMenuTransition = useTransition(!skillContext.hasChanges, {
    initial: { opacity: 1, transformY: "0px" },
    from: { opacity: 1, transformY: "-90px" },
    enter: { opacity: 1, transformY: "0px" },
    leave: { opacity: 0, transformY: "-90px" },
  });

  const fetchSkillsForPage = (page) => {
    dispatch(fetchSkills({
      query: "",
      parent: null,
      page: page,
      limit: 10
    }));
  }

  const handleLoadMoreSkills = () => {
    if (nextPage) {
      fetchSkillsForPage(nextPage);
    }
  }

  const handleAddSkill = () => {
    navigate('/settings/skills/new');
  }

  const handleSkillChange = (skill: Skill | null) => {
    setSkillContext((prev) => ({
      ...prev,
      skill: skill,
    }));

    const source = prepareSaveRequest(skill || {});
    const target = prepareSaveRequest(currentSkill || {});
    const changed = !_.isEqual(source, target);
    handleHasChanges(changed);
  }

  const handleHasChanges = (hasChanges: boolean) => {
    setSkillContext((prev) => ({
      ...prev,
      hasChanges: hasChanges,
    }));
  }

  const prepareSaveRequest = (skill) => {
    const request = {
      id: skill.id == "new" ? null : skill.id,
      name: skill.name,
      order: 0,
      subSkills: (skill.children || []).map((child, index) => {
        return {
          id: child.id || null,
          name: child.name,
          order: index + 1,
        };
      }),
    }

    return request;
  }

  const validateErrors = (skill) => {
    if (!skill.name || _.size(skill.name.trim()) === 0) {
      return true;
    }

    if (_.size(skill.name) > 200) {
      return true;
    }

    if (skill.children && skill.children.length > 0) {
      const subSkillsErrors = skill.children.map((subSkill) => {
        if (!subSkill.name || _.size(subSkill.name.trim()) === 0) {
          return true;
        }
        if (_.size(subSkill.name) > 200) {
          return true;
        }
        return false;
      });

      if (subSkillsErrors.includes(true)) {
        return true;
      }
    }

    return false;
  }

  const handleSave = () => {
    const skill = skillContext.skill;
    if (!skill) {
      return;
    }

    if (validateErrors(skill)) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    // Handle save logic here
    const request = prepareSaveRequest(skill);
    if (request.id === null) {
      dispatch(createSkill(request));
    } else {
      dispatch(updateSkill(request));
    }
  }

  const handleSaveCancel = () => {
    if (id === "new") {
      setSkillContext((prev) => ({
        ...prev,
        skill: null,
        hasChanges: false,
      }));

      const firstSkill = skills[0];
      if(firstSkill) {
        navigate(`/settings/skills/${firstSkill.id}`);
      }else { 
        navigate('/settings/skills');
      }
    } else {
      setSkillContext((prev) => ({
        ...prev,
        skill: currentSkill,
        hasChanges: false,
      }));
    }
  }

  return (
    <SkillContext.Provider value={skillContext}>
      <div className={cx(styles.skillsSettingsPage)}>
        <div className={styles.top}>
          <div className={cx(styles.header)}>
            <h1 className={cx(styles.title)}>Skills Settings</h1>
            <p>Manage your skills and preferences here.</p>
          </div>
          <div className={styles.menubar}>
            {addMenuTransition((style, item) => item && (
              <animated.div className={styles.addMenu} style={style}>
                <Actions.Row>
                  <Actions.Button
                    className={styles.addSkill}
                    width={3}
                    icon="paperflite-add"
                    title="add skill"
                    onClick={handleAddSkill}
                  />
                </Actions.Row>
              </animated.div>
            ))}

            <Reveal show={skillContext.hasChanges} className={styles.saveMenu}>
              <div className={styles.actions}>
                <div className={styles.message}>
                  click here to
                </div>
                <Button
                  variant="primary"
                  icon="paperflite-tick"
                  disabled={saving || creating}
                  text={(saving || creating) ? "Saving..." : "Save"}
                  onClick={handleSave}
                />
                <Button variant="secondary" text="Cancel" disabled={saving || creating} onClick={handleSaveCancel} />
              </div>

            </Reveal>
          </div>
        </div>
        <div className={cx(styles.content)}>
          <div className={cx(styles.left)}>
            <InfiniteScroll
              className={styles.skills}
              hasMore={hasMoreItems}
              loadMore={handleLoadMoreSkills}
              loading={loading}
              loader={() => {
                return _.times(_.size(skills) === 0 ? 4 : 1, (index) => (
                  <PlaceholderSkillRow key={index} animate={true} />
                ))
              }}
            >
              {skills.map((skill) => (
                <SkillRow key={skill.id} skill={skill} active={skill.id === id} />
              ))}
              {!loading && !skills.length && (
                <>
                  {_.times(4, (index) => (
                    <PlaceholderSkillRow key={index} animate={false} />
                  ))}
                </>
              )}
            </InfiniteScroll>
          </div>
          <div className={cx(styles.right)}>
            {!loading && !skills.length && !skillContext.skill && !id ? (<EmptySkillsPage />) : (
              <Outlet />
            )}
          </div>
        </div>
      </div>
    </SkillContext.Provider>
  );
}

interface SkillRowProps {
  skill: Skill;
  active: boolean;
}

export const SkillRow: React.FC<SkillRowProps> = ({ skill, active }) => {
  return (
    <div className={cx(styles.skillRow, { [styles.active]: active })}>
      <Link to={`/settings/skills/${skill.id}`} className={styles.link}>
        <Text className={styles.name} text={skill.name} lines={1} />
      </Link>
      <div className={styles.metadatas}>
        <span className={cx(styles.metadata, styles.subskills)}>{_.size(skill.children)} sub {pluralize("skill", _.size(skill.children))}</span>
        <span className={styles.divider}></span>
        <span className={cx(styles.metadata, styles.updatedDate)}>Last updated: <TimeAgo date={skill.modifiedDate} /></span>
      </div>
    </div>
  );
}

export const PlaceholderSkillRow: React.FC<{ animate?: boolean }> = ({ animate = true }) => {
  return (
    <div className={cx(styles.skillRow, styles.placeholder, { [styles.static]: !animate })}>
      <div className={cx(styles.name, styles.block)} />
      <div className={styles.metadatas}>
        <span className={cx(styles.metadata, styles.subskills, styles.block)}></span>
        <span className={styles.divider}></span>
        <span className={cx(styles.metadata, styles.updatedDate, styles.block)}></span>
      </div>
    </div>
  );
}

const EmptySkillsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.emptySkillsContainer}>
      <EmptyMessage
        className={styles.emptyMessage}
        image="/images/settings/custom_fields/empty.svg"
        heading="No skills created yet."
        message="Please create a new skill."
        action={{
          title: "Create Skill",
          icon: "paperflite-add",
          onClick: () => {
            navigate("/settings/skills/new");
          },
          variant: "primary"
        }}
      />
    </div>
  );
}

export const SkillDetailPage: React.FC = () => {
  const { id } = useParams();

  const skillContext = useContext(SkillContext);
  const { skill } = skillContext;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { loading, skills } = useSelector((state) => {
    const skillsState = _.get(state, 'coaching.settings.skills');
    const skills: Skill[] = _.get(skillsState, 'items') || [];
    const loading = _.get(skillsState, 'loading') || false;

    return {
      loading,
      skills,
    };
  }, _.isEqual);

  const handleSkillChange = (updatedSkill: Skill) => {
    skillContext.onChange(updatedSkill);
  }

  const handleSkillDelete = () => {
    setShowDeleteDialog(true);
  }

  const handleDeleteFinish = () => {
    setShowDeleteDialog(false);
  }

  useEffect(() => {

    // Update skill context when id or skills change
    if (id === "new" && (skill === null || skill.id !== "new")) {
      skillContext.onChange({
        id: "new",
        uuid: _.uniqueId('skill_'),
        name: "New Skill",
        children: []
      });

      skillContext.setHasChanges(true);
    } else if (id && id !== "new" && (skill === null || skill.id !== id)) {
      const skill = skills.find((s: Skill) => s.id === id);
      if (skill) {
        skillContext.onChange(skill);
        skillContext.setHasChanges(false);
      } else {
        skillContext.onChange(null);
        skillContext.setHasChanges(false);
      }
    }
  }, [id, skills, skill]);

  if (loading && !skill) {
    return <div className={styles.loadingContainer}>Loading skills...</div>;
  }

  if (!loading && _.size(skills) === 0 && !skill) {
    return <EmptySkillsPage />;
  }

  if (!skill) {
    return (
      <EmptyMessage
        className={styles.skillNotFoundContainer}
        image="/images/settings/custom_fields/empty.svg"
        heading="Skill not found."
        message="Please select a skill from the list."
      />
    );
  }

  return (
    <div className={cx(styles.skillDetailPage)}>
      <EditSkillForm skill={skill} onChange={handleSkillChange} onDelete={handleSkillDelete} />
      <DeleteSkillDialog
        show={showDeleteDialog}
        skill={skill}
        onFinish={handleDeleteFinish}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
      />
    </div>
  );
}

interface EditSkillFormProps {
  skill: Skill;
  // eslint-disable-next-line no-unused-vars
  onChange: (skill: Skill) => void;
  onDelete?: () => void;
}

export const EditSkillForm: React.FC<EditSkillFormProps> = ({ skill, onChange, onDelete }) => {
  const [draggingSkill, setDraggingSkill] = useState<Skill | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const id = useMemo(() => skill.id, [skill]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddSubSkill = () => {
    const newSubSkill: Skill = {
      uuid: _.uniqueId('subskill_'),
      parent: skill.id,
      name: 'New Skill',
      children: []
    };

    onChange({
      ...skill,
      children: [...(skill.children || []), newSubSkill]
    });
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...skill,
      name: e.target.value
    });
  };

  const handleSubSkillChange = (subSkillId: string, value: string) => {
    const updatedChildren = skill.children.map((subSkill) => {
      if (subSkill.uuid === subSkillId) {
        return { ...subSkill, name: value };
      }
      return subSkill;
    });

    onChange({ ...skill, children: updatedChildren });
  };

  const handleSubSkillDelete = (subSkillId: string) => {
    const updatedChildren = skill.children.filter((subSkill) => subSkill.uuid !== subSkillId);
    onChange({ ...skill, children: updatedChildren });
  };

  const handleDragStart = (event: any) => {
    const { active } = event;
    if (active.id) {
      const skillToDrag = skill.children.find((child) => child.uuid === active.id);
      if (skillToDrag) {
        setDraggingSkill(skillToDrag);
      }
    }
  }

  const handleDragOver = (event: any) => {
    const { active, over } = event;

    if (active.id === over.id) {
      return;
    }

    const oldIndex = skill.children.findIndex((child) => child.uuid === active.id);
    const newIndex = skill.children.findIndex((child) => child.uuid === over.id);

    const newChildren = [...skill.children];
    const [movedItem] = newChildren.splice(oldIndex, 1);
    newChildren.splice(newIndex, 0, movedItem);

    onChange({ ...skill, children: newChildren });
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    setDraggingSkill(null);

    if (active.id !== over.id) {
      const oldIndex = skill.children.findIndex((child) => child.uuid === active.id);
      const newIndex = skill.children.findIndex((child) => child.uuid === over.id);

      const newChildren = [...skill.children];
      const [movedItem] = newChildren.splice(oldIndex, 1);
      newChildren.splice(newIndex, 0, movedItem);

      onChange({ ...skill, children: newChildren });
    }
  }

  const validateErrors = (skill) => {
    let error: string | null = null;
    if (!skill.name || _.size(skill.name.trim()) === 0) {
      error = "Skill name is required";
    } else if (_.size(skill.name) > 200) {
      error = "Skill name must be less than 200 characters";
    }

    setNameError(error);
  }

  useEffect(() => {
    setDraggingSkill(null);
  }, [id]);

  useEffect(() => {
    if (skill) {
      validateErrors(skill);
    }
  }, [skill])

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}>
      <div className={cx(styles.editSkillForm)}>
        <div className={cx(styles.row)}>
          <div className={cx(styles.field, styles.name)}>
            <Input
              className={styles.input}
              placeholder="Enter skill name..."
              value={skill?.name || ""}
              onChange={handleNameChange}
            />
            <Reveal show={nameError != null}>
              <div className={styles.error}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>{nameError}</span>
              </div>
            </Reveal>
          </div>
          {skill?.id !== "new" && (
          <Tooltip content="Delete Skill" placement="left">
            <button className={styles.deleteButton} onClick={onDelete}>
              <i className={cx(styles.icon, "paperflite-delete")}></i>
            </button>
          </Tooltip>
          )}
        </div>
        <div className={cx(styles.field, styles.subSkills)}>
          <div className={cx(styles.title)}>Sub Skills</div>
          <div className={cx(styles.list)}>
            <button className={cx(styles.addButton)} onClick={handleAddSubSkill}>
              <i className={cx(styles.icon, "paperflite-add")}></i>
              <span>Add Sub Skill</span>
            </button>

            {_.size(skill.children) > 0 && (
              <SortableContext items={skill.children.map((child) => child.uuid!)} strategy={rectSwappingStrategy}>
                {skill.children?.map((subSkill) => (
                  <SubSkill key={subSkill.id} skill={subSkill} onChange={handleSubSkillChange} onDelete={() => handleSubSkillDelete(subSkill.uuid!)} />
                ))}
              </SortableContext>
            )}
          </div>
        </div>
      </div>
      <DragOverlay adjustScale={false} dropAnimation={null}>
        {draggingSkill ? (
          <div className={cx(styles.draggingSkill)}>
            <i className={cx(styles.handle, "paperflite-drag")}></i>
            <div className={cx(styles.name)}>{draggingSkill.name}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

interface SubSkillProps {
  skill: Skill;
  // eslint-disable-next-line no-unused-vars
  onChange: (id: string, name: string) => void;
  onDelete: () => void;
}

const SubSkill: React.FC<SubSkillProps> = ({ skill, onChange, onDelete }) => {
  const name = useMemo(() => skill.name || "", [skill]);
  const [nameError, setNameError] = useState<string | null>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useSortable({ id: skill.uuid! });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(skill.uuid!, e.target.value);
  };

  const validateErrors = (skill) => {
    let error: string | null = null;
    if (!skill.name || _.size(skill.name.trim()) === 0) {
      error = "Skill name is required";
    } else if (_.size(skill.name) > 200) {
      error = "Skill name must be less than 200 characters";
    }

    setNameError(error);
  }

  useEffect(() => {
    if (skill) {
      validateErrors(skill);
    }
  }, [skill])

  return (
    <div className={cx(styles.subSkill, {
      [styles.dragging]: isDragging,
      [styles.error]: nameError
    })} ref={setNodeRef}>
      <i className={cx(styles.handle, "paperflite-drag")} {...attributes} {...listeners}></i>
      <AutoGrowInput className={styles.name} value={name} onChange={handleNameChange} />
      <div className={styles.actions}>
        <div className={styles.divider}></div>
        {nameError &&
          <Tooltip content={nameError} placement='top'>
            <i className={cx(styles.errorIcon, "fa-solid", "fa-triangle-exclamation")}></i>
          </Tooltip>
        }
        <button className={cx(styles.deleteButton)} onClick={onDelete}>
          <i className={cx(styles.icon, "paperflite-delete")}></i>
        </button>
      </div>
    </div>
  )
}

interface DeleteSkillDialogProps {
  show: boolean;
  skill: Skill;
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteSkillDialog: React.FC<DeleteSkillDialogProps> = ({ show, skill, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { deleting, deleted, nextSkill } = useSelector((state: any) => {
    const skills = _.get(state, 'coaching.settings.skills.items').filter((s: Skill) => s.id !== skill.id);
    let nextSkill = null;

    if (_.size(skills) > 0) {
      nextSkill = _.get(skills, '[0].id') || null;
    }

    return {
      deleting: _.get(state, 'coaching.settings.skills.deletion.deleting', false),
      deleted: _.get(state, 'coaching.settings.skills.deletion.deleted', false),
      nextSkill: nextSkill
    };
  });

  const handleConfirm = () => {
    if (!skill) {
      return;
    }

    dispatch(deleteSkill(skill.id!)); // Dispatch the delete action with skill id
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!deleting && deleted) {
      dispatch(invalidateDeleteSkill());
      toast.success("Skill deleted successfully!");

      if (nextSkill) {
        navigate(`/settings/skills/${nextSkill}`);
      } else {
        navigate('/settings/skills');
      }

      if (onFinish) {
        onFinish();
      }
    }
  }, [deleting, deleted, nextSkill]);

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        title="deleting..."
        message={`${skill.name}`}
      >
      </Slider.Header>
      <Slider.Body>
        <p className={styles.description} style={{ marginBottom: 10 }}>
          Are You Sure? Do you really want to delete this skill? No takebacksies!
        </p>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          variant="primary"
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};
